import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from 'react-toastify';
import { MAX_API_RETRIES } from '../constants';
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import * as action from '../Action/Users/Users_Action';
import NumberDisplay from "./NumberDisplay/Index";
import { useAuth } from "../context/Context";
import { usePermission } from "../hooks/usePermission";

const Users = () => {
    const { user } = useAuth();
    const { can } = usePermission();
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const dataTableInstanceRef = useRef(null);
    const navigate = useNavigate();
    const [count, setCount] = useState(0);
    const [fetchError, setFetchError] = useState(null);
    const reloadRef = useRef(null);
    const pageToRestoreRef = useRef(null);
    const retryCountRef = useRef(0);

    const destroyTable = useCallback(() => {
        if (dataTableInstanceRef.current) {
            try { dataTableInstanceRef.current.destroy(); } catch (_) {}
            dataTableInstanceRef.current = null;
        }
    }, []);

    const initTable = useCallback((rows) => {
        const $ = window.$ || window.jQuery;
        if (!$ || !tableRef.current) return;

        destroyTable();

        dataTableInstanceRef.current = $(tableRef.current).DataTable({
            data: rows,
            destroy: true,
            scrollX: true,
            pageLength: 10,
            lengthMenu: [[10, 20, 50, 100, -1], [10, 20, 50, 100, 'All']],
            order: [[2, 'asc']],
            language: {
                search: 'Search:',
                lengthMenu: 'Show _MENU_ entries',
                info: 'Showing _START_ to _END_ of _TOTAL_ users',
                infoEmpty: 'No users found',
                zeroRecords: 'No matching users found',
            },
            columns: [
                {
                    data: null,
                    title: 'No',
                    width: '50px',
                    orderable: false,
                    render: (data, type, row, meta) =>
                        meta.row + meta.settings._iDisplayStart + 1,
                },
                {
                    data: null,
                    title: 'Image',
                    orderable: false,
                    render: (data, type, row) => {
                        const img = row.profile_picture || row.avatar || row.photo_url || row.image_url || '';
                        return `<div class="d-flex align-items-center justify-content-center">
                            <img src="${img || '/assets/images/profil-img-1.png'}"
                                 onerror="this.onerror=null;this.src='/assets/images/profil-img-1.png';"
                                 alt="User" class="tbl_img" />
                         </div>`;
                    },
                },
                {
                    data: 'id',
                    title: 'User ID',
                    render: (data, type) => {
                        if (type === 'sort' || type === 'filter') return Number(data) || 0;
                        return data
                            ? `<span style="font-size:12px;color:#c8c8d8">${data}</span>`
                            : '—';
                    },
                },
                {
                    data: null,
                    title: 'Display Name',
                    render: (data, type, row) => {
                        const name = row.display_name || row.name || row.username || row.full_name || '';
                        if (type === 'sort' || type === 'filter') return name.toLowerCase();
                        return name
                            ? `<span title="${name}">${name.length > 18 ? name.slice(0, 18) + '…' : name}</span>`
                            : '<span style="color:#8a8a9a">—</span>';
                    },
                },
                {
                    data: 'email',
                    title: 'Email',
                    render: (data) =>
                        data && data !== 'null'
                            ? data
                            : '<span style="color:#8a8a9a;font-size:12px">—</span>',
                },
                {
                    data: 'login_type',
                    title: 'Login Type',
                    render: (data) => {
                        const icons = { mobile: 'fa-mobile-alt', google: 'fa-google', apple: 'fa-apple', email: 'fa-envelope' };
                        const icon = icons[data?.toLowerCase()] || 'fa-user';
                        return `<span><i class="fab ${icon}" style="margin-right:5px"></i>${data || '—'}</span>`;
                    },
                },
                {
                    data: 'current_plan',
                    title: 'Plan',
                    render: (data) => {
                        const colors = { Free: '#8a8a9a', Weekly: '#f59e0b', Yearly: '#6366f1', VIP: '#f82581' };
                        const c = colors[data] || '#8a8a9a';
                        return `<span style="color:${c};font-weight:600">${data || 'Free'}</span>`;
                    },
                },
                {
                    data: 'coins',
                    title: 'Coins',
                    render: (data, type) => {
                        if (type === 'sort') return Number(data) || 0;
                        return `<span>${Number(data || 0).toLocaleString()}</span>`;
                    },
                },
                {
                    data: 'wallet_balance',
                    title: 'Wallet',
                    render: (data, type) => {
                        if (type === 'sort') return Number(data) || 0;
                        return `<span>$${Number(data || 0).toFixed(2)}</span>`;
                    },
                },
                {
                    data: 'created_at',
                    title: 'Joined',
                    render: (data, type) => {
                        if (!data) return type === 'sort' ? '' : '—';
                        if (type === 'sort') return data;
                        const d = new Date(data);
                        return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
                    },
                },
                {
                    data: null,
                    title: 'Status',
                    orderable: false,
                    render: (data, type, row) =>
                        `<div class="checkbox-slider d-flex align-items-center">
                            <label>
                                <input type="checkbox" class="d-none hideContent is_blocked" ${row.is_blocked ? 'checked' : ''} />
                                <span class="toggle_background">
                                    <div class="circle-icon"></div>
                                    <div class="vertical_line"></div>
                                </span>
                            </label>
                         </div>`,
                },
                {
                    data: null,
                    title: 'Action',
                    orderable: false,
                    render: () =>
                        `<div class="d-flex gap-2">
                            <button class="btn btn-sm btn-primary view-btn" title="View User">
                                <i class="fas fa-eye"></i>
                            </button>
                         </div>`,
                },
            ],
        });

        if (pageToRestoreRef.current !== null) {
            const pg = pageToRestoreRef.current;
            pageToRestoreRef.current = null;
            setTimeout(() => {
                if (dataTableInstanceRef.current) {
                    dataTableInstanceRef.current.page(pg).draw(false);
                }
            }, 0);
        }

        $(tableRef.current).off('click', '.view-btn').on('click', '.view-btn', function () {
            const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
            if (rowData) navigate('/users/' + rowData.id);
        });

        $(tableRef.current).off('click', '.is_blocked').on('click', '.is_blocked', function () {
            if (!can('users', 'block')) {
                toast.error("You don't have permission to block/unban users.");
                return false;
            }
            const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
            if (!rowData) return;
            if (dataTableInstanceRef.current) {
                pageToRestoreRef.current = dataTableInstanceRef.current.page();
            }
            dispatch(action.updateIsBlockedUser({ id: rowData.id, is_blocked: rowData.is_blocked ? '0' : 1 }))
                .then(() => { if (reloadRef.current) reloadRef.current(); })
                .catch(() => toast.error('Something went wrong!'));
        });
    }, [destroyTable, dispatch, navigate, user, can]);

    const loadUsers = useCallback(() => {
        reloadRef.current = loadUsers;
        setFetchError(null);
        dispatch(action.getUsersList({ page: 1, limit: 1000, search: '' }))
            .then((response) => {
                retryCountRef.current = 0;
                const details = response?.responseDetails || response || {};
                const rows = Array.isArray(details) ? details : (details.data || details.rows || []);
                const total = details.totalRecords || details.total || rows.length;
                setCount(total);
                initTable(rows);
            })
            .catch((err) => {
                console.error('Users list error:', err);
                retryCountRef.current += 1;
                const status = err?.statusCode || err?.response?.status;
                const msg = err?.message || err?.responseMessage || 'An unexpected error occurred. Please try again.';
                if (retryCountRef.current >= MAX_API_RETRIES) {
                    setFetchError({ status, msg });
                    return;
                }
                setFetchError({ status, msg });
            });
    }, [dispatch, initTable]);

    const loadUsersRef = useRef(loadUsers);
    loadUsersRef.current = loadUsers;

    useEffect(() => {
        retryCountRef.current = 0;
        loadUsersRef.current();
        return () => destroyTable();
    }, []);

    return (
        <section id="users-section" className="section">
            <div className="card">
                <div className="card-header">
                    <div className="page-title w-100">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4>User List (<span className="total_user"><NumberDisplay count={count} /></span>)</h4>
                        </div>
                    </div>
                </div>
                <div className="card-body" style={{ paddingBottom: '80px' }}>
                    {fetchError && (
                        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
                            <span>
                                <i className="fas fa-exclamation-circle me-2" />
                                <strong>Failed to load users</strong>
                                {fetchError.status ? ` (HTTP ${fetchError.status})` : ''} — {fetchError.msg}
                            </span>
                            <button className="btn btn-sm btn-outline-danger ms-3" onClick={() => { retryCountRef.current = 0; loadUsers(); }}>
                                <i className="fas fa-redo me-1" /> Retry
                            </button>
                        </div>
                    )}
                    <table
                        ref={tableRef}
                        className="user-table table table-striped table-bordered w-100 dataTable no-footer"
                        id="usersTables"
                        role="grid"
                    ></table>
                </div>
            </div>
        </section>
    );
};

export default Users;
